import { useState } from 'react';
import { useAuthStore } from '@/store/auth/auth.store';
import { useUiStore } from '@/store/ui';
import { AlertSeverityEnum } from '@/shared/interfaces';
import {
  useCrearRecepcionEquipo,
  useFetchCostosEquipo,
  useFetchMotivosExcepcion,
  useFetchMotivosRetiro,
  useFetchTiposIncidencia,
  useFetchRecepcionesByContrato,
  useObtenerEquiposCliente,
  useFinalizarRetiro,
} from '@/shared/actions/erp/tickets-soporte-cliente/recepcion-equipos.actions';
import type { RecepcionEquipoResponse, EquipoClienteDTO } from '@/shared/actions/erp/tickets-soporte-cliente/recepcion-equipos.actions';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Stack,
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Radio,
  Collapse,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/HourglassEmpty';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';

type TabRecepcionEquiposProps = {
  contrato: any;
  cliente: any;
  estadoServicioColor: (raw?: string | null) => 'success' | 'error' | 'default';
  can: (perm: string) => boolean;
};

export default function TabRecepcionEquipos({
  contrato,
  cliente,
  estadoServicioColor,
  can,
}: TabRecepcionEquiposProps) {
  const user = useAuthStore((s) => s.user);
  const setAlert = useUiStore((s) => s.setAlert);

  const [tipoIncidenciaId, setTipoIncidenciaId] = useState<number | null>(null);
  const [motivoRetiroId, setMotivoRetiroId] = useState<number | null>(null);
  const [equiposRetiro, setEquiposRetiro] = useState<Array<{ id?: string | number; modelo: string; serie: string; mac: string; estadoFisico: string; marca?: string | null; }>>([]);
  const [equipoSeleccionado, setEquipoSeleccionado] = useState<EquipoClienteDTO | null>(null);
  const [modeloEquipo, setModeloEquipo] = useState('');
  const [serieEquipo, setSerieEquipo] = useState('');
  const [direccionMAC, setDireccionMAC] = useState('');
  const [estadoFisicoAgregar, setEstadoFisicoAgregar] = useState('');
  const [detalleTecnico, setDetalleTecnico] = useState('');
  const responsable = (user as any)?.username || '';
  const fechaRecepcion = new Date().toISOString().split('T')[0];
  const [evidencias, setEvidencias] = useState<File[]>([]);
  const [evidenciaPreviews, setEvidenciaPreviews] = useState<string[]>([]);
  const [cobroModalOpen, setCobroModalOpen] = useState(false);
  const [procesarConfirmado, setProcesarConfirmado] = useState(false);
  const [retiroFinalizado, setRetiroFinalizado] = useState(false);

  const crearRecepcion = useCrearRecepcionEquipo();
  const finalizarRetiroApi = useFinalizarRetiro();
  const contratoCodigo = contrato?.codigo || '';
  const { data: historialRes, refetch: refetchHistorial } = useFetchRecepcionesByContrato(contratoCodigo);
  const historial: RecepcionEquipoResponse[] = (historialRes as any)?.data ?? [];
  const [expandedHistorial, setExpandedHistorial] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);

  const { data: tiposRes, isLoading: tiposLoading } = useFetchTiposIncidencia();
  const tiposIncidencia = (tiposRes as any)?.data ?? [];

  const { data: motivosRes, isLoading: motivosLoading } = useFetchMotivosRetiro(tipoIncidenciaId);
  const motivosRetiro = (motivosRes as any)?.data ?? [];

  const { data: costosRes } = useFetchCostosEquipo();
  const costosCatalogo = (costosRes as any)?.data ?? [];

  const { data: motivosExcepcionRes } = useFetchMotivosExcepcion();
  const motivosExcepcionCatalogo = (motivosExcepcionRes as any)?.data ?? [];

  const handleTipoIncidenciaChange = (id: number | null) => {
    setTipoIncidenciaId(id);
    setMotivoRetiroId(null);
  };

  const FORMATOS_PERMITIDOS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
  const handleEvidenciasUpload = (files: FileList | null) => {
    if (!files) return;
    const nuevos = Array.from(files).filter(f => {
      const ext = '.' + f.name.split('.').pop()?.toLowerCase();
      if (!FORMATOS_PERMITIDOS.includes(ext)) {
        setAlert(true, `Formato no soportado: ${f.name}. Formatos aceptados: ${FORMATOS_PERMITIDOS.join(', ')}`, AlertSeverityEnum.warning);
        return false;
      }
      return true;
    });
    setEvidencias(prev => [...prev, ...nuevos]);
    nuevos.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setEvidenciaPreviews(prev => [...prev, e.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const eliminarEvidencia = (index: number) => {
    setEvidencias(prev => prev.filter((_, i) => i !== index));
    setEvidenciaPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = () => reject(new Error('Error al leer archivo'));
      reader.readAsDataURL(file);
    });

  const finalizarRetiro = async () => {
    if (equiposRetiro.length === 0) {
      setAlert(true, 'Debe agregar al menos un equipo al retiro', AlertSeverityEnum.warning);
      return;
    }
    if (!tipoIncidenciaId || !motivoRetiroId) {
      setAlert(true, 'Seleccione el tipo de incidencia y motivo del retiro', AlertSeverityEnum.warning);
      return;
    }
    if (!detalleTecnico) {
      setAlert(true, 'Complete el detalle técnico', AlertSeverityEnum.warning);
      return;
    }
    if (!responsable) {
      setAlert(true, 'Ingrese el responsable', AlertSeverityEnum.warning);
      return;
    }
    if (!fechaRecepcion) {
      setAlert(true, 'Seleccione la fecha de recepción', AlertSeverityEnum.warning);
      return;
    }
    if (evidencias.length === 0) {
      setAlert(true, 'Debe cargar al menos una evidencia fotográfica', AlertSeverityEnum.warning);
      return;
    }
    const allContractInRetiro = equiposCliente.every(eq =>
      equiposRetiro.some(r =>
        r.serie === eq.serie_num || (r.id != null && r.id === eq.id_detalle_contrato)
      )
    );
    if (!allContractInRetiro) {
      setAlert(true, 'Todos los equipos del contrato deben estar en la lista de equipos a retirar', AlertSeverityEnum.warning);
      return;
    }
    const evidenciasPayload = await Promise.all(
      evidencias.map(async (file) => ({
        nombre_original: file.name,
        base64: await fileToBase64(file),
        content_type: file.type || 'image/jpeg',
      })),
    );
    const fileToBase64Safe = async (file: File) => ({
      nombre_original: file.name,
      base64: await fileToBase64(file),
      content_type: file.type || 'application/octet-stream',
    });

    const itemsCobrar = procesarEquipos.filter(e => e.cobrar).flatMap(e =>
      e.costos.map(costo => {
        const subtotal = costo.valorUnitario * costo.cantidad;
        const iva = subtotal * 0.15;
        const total = subtotal + iva;
        return {
          concepto: `${costo.item} - ${e.modelo || 'Equipo'} ${e.serie}`,
          cantidad: costo.cantidad,
          valor_unitario: costo.valorUnitario,
          subtotal,
          iva,
          total,
          serie: e.serie,
        };
      })
    );

    const itemsExcepcion = await Promise.all(
      procesarEquipos.filter(e => e.excepcion).map(async (e) => ({
        concepto: `${e.modelo || 'Equipo'} ${e.serie}`,
        cantidad: 1,
        motivo: e.excepcionMotivo,
        descripcion_motivo: e.excepcionMotivo === 'Otro' ? e.excepcionDescripcion : null,
        detalle: e.excepcionDetalle || `${e.modelo || 'Equipo'} - Serie: ${e.serie}`,
        archivos: e.excepcionArchivos.length > 0
          ? await Promise.all(e.excepcionArchivos.map(fileToBase64Safe))
          : undefined,
      })),
    );

    const tipoServicioStr = contrato?.tipo_servicio || '';
    const tipoServicioVal = tipoServicioStr.includes('Fibra') ? 2 : 1;

    try {
      const res = await crearRecepcion.mutateAsync({
        id_contrato: contratoCodigo,
        tipo_incidencia_id: tipoIncidenciaId,
        motivo_retiro_id: motivoRetiroId,
        responsable,
        fecha_recepcion: fechaRecepcion,
        detalle_tecnico: detalleTecnico || null,
        tipo_servicio: tipoServicioVal,
        id_plan_internet: (contrato as any)?.id_plan_internet ?? 0,
        equipos: equiposRetiro.map((eq) => ({
          id_detalle_contrato: eq.id != null ? String(eq.id) : null,
          modelo: eq.modelo,
          marca: eq.marca || null,
          serie: eq.serie,
          direccion_mac: eq.mac || null,
          estado_fisico: eq.estadoFisico,
        })),
        evidencias: evidenciasPayload,
        cobros: itemsCobrar.length > 0 ? itemsCobrar : undefined,
        excepciones: itemsExcepcion.length > 0 ? itemsExcepcion : undefined,
      });

      const raw = res as any;
      const recepcionId: string | null = raw?.data?.id || raw?.id || raw?.data?.data?.id || null;
      if (!recepcionId) return;

      await finalizarRetiroApi.mutateAsync({ id: recepcionId, idContrato: contratoCodigo });
      setRetiroFinalizado(true);
      refetchHistorial();
    } catch {
      // error handled by mutation callbacks
    }
  };

  const eliminarEquipoRetiro = (index: number) => {
    const eq = equiposRetiro[index];
    if (eq) {
      setProcesarEquipos(prev => prev.filter(p => p.serie !== eq.serie));
    }
    setEquiposRetiro(prev => prev.filter((_, i) => i !== index));
  };

  const seleccionarEquipoContrato = (eq: EquipoClienteDTO) => {
    setEquipoSeleccionado(eq);
    setModeloEquipo(eq.modelo || eq.equipo || '');
    setSerieEquipo(eq.serie_num || '');
    setDireccionMAC(eq.serie_mac || '');
    setEstadoFisicoAgregar('');
  };

  const agregarEquipoRetiro = () => {
    if (!serieEquipo || serieEquipo.length < 6) { setAlert(true, 'La serie del equipo debe tener al menos 6 caracteres', AlertSeverityEnum.warning); return; }
    if (!estadoFisicoAgregar) { setAlert(true, 'Seleccione el estado físico del equipo', AlertSeverityEnum.warning); return; }
    setEquiposRetiro(prev => [...prev, {
      id: equipoSeleccionado?.id_detalle_contrato ?? undefined,
      modelo: modeloEquipo,
      serie: serieEquipo,
      mac: direccionMAC,
      estadoFisico: estadoFisicoAgregar,
      marca: equipoSeleccionado?.marca,
    }]);
    setProcesarEquipos(prev => [...prev, {
      idx: prev.length,
      modelo: modeloEquipo,
      serie: serieEquipo,
      mac: direccionMAC,
      marca: equipoSeleccionado?.marca,
      cobrar: false,
      costos: [],
      excepcion: false,
      excepcionMotivo: '',
      excepcionDescripcion: '',
      excepcionDetalle: '',
      excepcionArchivos: [],
      excepcionArchivosPreviews: [],
      _collapsed: false,
    }]);
    setEquipoSeleccionado(null);
    setModeloEquipo('');
    setSerieEquipo('');
    setDireccionMAC('');
    setEstadoFisicoAgregar('');
  };

  // Modal Procesar Equipos - combina cobro y excepción por equipo
  interface CostoSeleccionado {
    id: number;
    item: string;
    valorUnitario: number;
    cantidad: number;
  }

  interface ProcesarEquipoItem {
    idx: number;
    modelo: string;
    serie: string;
    mac: string;
    marca?: string | null;
    cobrar: boolean;
    costos: CostoSeleccionado[];
    excepcion: boolean;
    excepcionMotivo: string;
    excepcionDescripcion: string;
    excepcionDetalle: string;
    excepcionArchivos: File[];
    excepcionArchivosPreviews: string[];
    _collapsed: boolean;
  }

  const [procesarEquipos, setProcesarEquipos] = useState<ProcesarEquipoItem[]>([]);

  const abrirProcesarEquipos = () => {
    const nuevos = equiposRetiro.map((eq, i) => {
      const existente = procesarEquipos.find(p => p.serie === eq.serie);
      if (existente) return { ...existente, idx: i };
      return {
        idx: i,
        modelo: eq.modelo,
        serie: eq.serie,
        mac: eq.mac,
        marca: eq.marca,
        cobrar: false,
        costos: [],
        excepcion: false,
        excepcionMotivo: '',
        excepcionDescripcion: '',
        excepcionDetalle: '',
        excepcionArchivos: [],
        excepcionArchivosPreviews: [],
        _collapsed: false,
      };
    });
    setProcesarEquipos(nuevos);
    setCobroModalOpen(true);
  };

  const procesarEquiposHandler = () => {
    const itemsCobrar = procesarEquipos.filter(e => e.cobrar);
    const itemsExcepcion = procesarEquipos.filter(e => e.excepcion);

    if (itemsExcepcion.some(e => !e.excepcionMotivo)) {
      setAlert(true, 'Todos los equipos marcados como excepción deben tener un motivo', AlertSeverityEnum.warning);
      return;
    }

    if (itemsExcepcion.some(e => e.excepcionMotivo === 'Otro' && !e.excepcionDescripcion)) {
      setAlert(true, 'Los equipos con motivo "Otro" deben incluir una descripción del motivo', AlertSeverityEnum.warning);
      return;
    }

    if (itemsExcepcion.some(e => !e.excepcionDetalle)) {
      setAlert(true, 'Todos los equipos con excepción deben tener un detalle de la situación', AlertSeverityEnum.warning);
      return;
    }

    if (itemsCobrar.some(e => e.costos.length === 0)) {
      setAlert(true, 'Todos los equipos marcados como cobrar deben tener al menos un costo agregado', AlertSeverityEnum.warning);
      return;
    }

    setProcesarConfirmado(true);
    setCobroModalOpen(false);
  };

  const { data: equiposClienteRes } = useObtenerEquiposCliente({
    enabled: !!contrato,
    params: {
      clienteId: (cliente as any)?.id_cliente,
      contratoId: contrato?.id_contrato,
    },
  });
  const equiposCliente: EquipoClienteDTO[] = ((equiposClienteRes as any)?.data ?? []).filter((eq: { cantidad: any; observaciones: string; }) => {
    const cant = parseInt(eq.cantidad ?? '0', 10);
    return !isNaN(cant) && cant > 0 && eq.observaciones === 'instalado';
  });

  const estadoRetiro = contrato?.estado_servicio || contrato?.estado_contrato || '';
  const equiposRetirados = estadoRetiro === 'Equipos Retirados';

  return (
    <>
      {(() => {
        if (equiposRetirados && historial.length === 0 && !retiroFinalizado) {
          return (
            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, textAlign: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h6" fontWeight={600}>Equipos ya retirados</Typography>
              <Typography variant="body2" color="text.secondary">
                Los equipos de este contrato ya fueron retirados anteriormente.
              </Typography>
            </Paper>
          );
        }
        if (!equiposRetirados && historial.length === 0 && !retiroFinalizado) {
          return (
            <Stack spacing={2}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <ElectricalServicesIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>Retiro de Equipos</Typography>
                  <Chip label={estadoRetiro || 'N/A'} size="small" color={estadoServicioColor(estadoRetiro)} sx={{ ml: 'auto', fontWeight: 600 }} />
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo Incidencia</InputLabel>
                      <Select value={tipoIncidenciaId ?? ''} label="Tipo Incidencia" onChange={(e) => handleTipoIncidenciaChange(e.target.value === '' ? null : Number(e.target.value))}>
                        <MenuItem value="">Seleccione</MenuItem>
                        {tiposIncidencia.map((t: { id: number; nombre: string }) => (
                          <MenuItem key={t.id} value={t.id}>{t.nombre}</MenuItem>
                        ))}
                      </Select>
                      {tiposLoading && <CircularProgress size={16} sx={{ position: 'absolute', right: 32, top: 14 }} />}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" disabled={!tipoIncidenciaId}>
                      <InputLabel>Motivo Retiro</InputLabel>
                      <Select value={motivoRetiroId ?? ''} label="Motivo Retiro" onChange={(e) => setMotivoRetiroId(e.target.value === '' ? null : Number(e.target.value))}>
                        <MenuItem value="">Seleccione</MenuItem>
                        {motivosRetiro.map((m: { id: number; nombre: string }) => (
                          <MenuItem key={m.id} value={m.id}>{m.nombre}</MenuItem>
                        ))}
                      </Select>
                      {motivosLoading && <CircularProgress size={16} sx={{ position: 'absolute', right: 32, top: 14 }} />}
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Equipos Cliente</Typography>

                {equiposCliente.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Equipos registrados en el contrato — seleccione uno para auto-completar:
                    </Typography>
                    <TableContainer sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Modelo</TableCell>
                            <TableCell>Marca</TableCell>
                            <TableCell>SERIE MAC</TableCell>
                            <TableCell>SERIE NUM</TableCell>
                            <TableCell>Cant.</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Seleccionar</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {equiposCliente.map((eq, idx) => {
                            const yaSeleccionado = equiposRetiro.some(
                              r => r.serie === eq.serie_num || (r.id != null && r.id === eq.id_detalle_contrato)
                            );
                            return (
                            <TableRow
                              key={eq.id_detalle_contrato ?? idx}
                              hover={!yaSeleccionado}
                              selected={equipoSeleccionado?.id_detalle_contrato === eq.id_detalle_contrato}
                              sx={{ cursor: yaSeleccionado ? 'not-allowed' : 'pointer', opacity: yaSeleccionado ? 0.5 : 1 }}
                              onClick={() => !yaSeleccionado && seleccionarEquipoContrato(eq)}
                            >
                              <TableCell><Typography variant="body2" fontWeight={600}>{eq.modelo || eq.equipo || '-'}</Typography></TableCell>
                              <TableCell>{eq.marca || '-'}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{eq.serie_mac || '-'}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{eq.serie_num || '-'}</TableCell>
                              <TableCell align="center">{eq.cantidad ?? '-'}</TableCell>
                              <TableCell>{eq.observaciones || '-'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={yaSeleccionado ? 'Ya añadido' : equipoSeleccionado?.id_detalle_contrato === eq.id_detalle_contrato ? 'Seleccionado' : 'Seleccionar'}
                                  size="small"
                                  color={yaSeleccionado ? 'default' : equipoSeleccionado?.id_detalle_contrato === eq.id_detalle_contrato ? 'primary' : 'default'}
                                  variant={yaSeleccionado ? 'filled' : 'outlined'}
                                />
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
                {equiposCliente.length === 0 && (
                  <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>Sin equipos asignados al contrato. Ingrese los datos manualmente.</Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Modelo Equipo" value={modeloEquipo} onChange={(e) => setModeloEquipo(e.target.value)} placeholder="Ej: ONT Huawei HG8245" InputProps={{ readOnly: !!(equipoSeleccionado?.modelo || equipoSeleccionado?.equipo) }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Serie Equipo" value={serieEquipo} onChange={(e) => setSerieEquipo(e.target.value.toUpperCase())} placeholder="Ingrese serie" required inputProps={{ minLength: 6 }} InputProps={{ readOnly: !!equipoSeleccionado?.serie_num }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Dirección MAC" value={direccionMAC} onChange={(e) => setDireccionMAC(e.target.value)} placeholder="Ingrese MAC" InputProps={{ readOnly: !!equipoSeleccionado?.serie_mac }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Estado Físico</InputLabel>
                      <Select value={estadoFisicoAgregar} label="Estado Físico" onChange={(e) => setEstadoFisicoAgregar(e.target.value)} required>
                        <MenuItem value="">Seleccione</MenuItem>
                        <MenuItem value="Bueno">Bueno</MenuItem>
                        <MenuItem value="Regular">Regular</MenuItem>
                        <MenuItem value="Dañado">Dañado</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button variant="contained" onClick={agregarEquipoRetiro} fullWidth>Agregar</Button>
                  </Grid>
                </Grid>

                {equiposRetiro.length > 0 && (
                  <TableContainer sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Equipos a retirar ({equiposRetiro.length})
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Modelo</TableCell>
                          <TableCell>Serie</TableCell>
                          <TableCell>MAC</TableCell>
                          <TableCell>Estado Físico</TableCell>
                          <TableCell>Acción</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {equiposRetiro.map((eq, i) => (
                          <TableRow key={i}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{eq.modelo || '-'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{eq.serie}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{eq.mac || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={eq.estadoFisico}
                                size="small"
                                color={eq.estadoFisico === 'Bueno' ? 'success' : eq.estadoFisico === 'Regular' ? 'warning' : 'error'}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" color="error" onClick={() => eliminarEquipoRetiro(i)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button variant="contained" color="error" onClick={abrirProcesarEquipos}>
                    {procesarConfirmado ? 'Editar Cobro / Excepción' : 'Cobro / Excepción (Opcional)'}
                  </Button>
                </Stack>

                {procesarConfirmado && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid', borderColor: '#bbf7d0' }}>
                    <Typography variant="caption" fontWeight={700} color="success.dark">Resumen de procesamiento</Typography>
                    {procesarEquipos.length === 0 && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>Sin equipos pendientes</Typography>
                    )}
                    {procesarEquipos.filter(e => e.cobrar).map((e, i) => (
                      e.costos.map((costo, ci) => {
                        const total = costo.valorUnitario * costo.cantidad * 1.15;
                        return (
                          <Typography key={`${i}-${ci}`} variant="caption" display="block" sx={{ mt: 0.5 }}>
                            {e.modelo || 'Equipo'} (Serie: {e.serie}) → <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>COBRAR</Box> {costo.item} x{costo.cantidad} — ${total.toFixed(2)}
                          </Typography>
                        );
                      })
                    ))}
                    {procesarEquipos.filter(e => e.excepcion).map((e, i) => (
                      <Typography key={`exc-${i}`} variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {e.modelo || 'Equipo'} (Serie: {e.serie}) → <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>EXCEPCIÓN</Box> ({e.excepcionMotivo})
                      </Typography>
                    ))}
                    {(() => {
                      const totalCobrar = procesarEquipos.filter(e => e.cobrar).reduce((s, e) => s + e.costos.reduce((sum, c) => sum + c.valorUnitario * c.cantidad, 0) * 1.15, 0);
                      if (totalCobrar <= 0) return null;
                      return (
                        <Typography variant="caption" display="block" sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: '#bbf7d0', fontWeight: 700, fontSize: 13 }}>
                          Total a cobrar: <Box component="span" sx={{ color: 'success.main' }}>${totalCobrar.toFixed(2)}</Box>
                        </Typography>
                      );
                    })()}
                  </Box>
                )}
              </Paper>

              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Evidencias Fotográficas</Typography>
                <Box
                  sx={{ border: '2px dashed #c7d2fe', borderRadius: 3, p: 3, textAlign: 'center', bgcolor: '#f8faff', mb: 2 }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleEvidenciasUpload(e.dataTransfer.files); }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="subtitle2" color="primary.main" gutterBottom>Adjunte fotografías del estado del equipo <Box component="span" color="error.main">*</Box></Typography>
                  <Typography variant="caption" color="text.secondary">Mínimo 1 foto. Formatos: JPG, PNG, GIF, WebP, BMP</Typography>
                  <Button variant="outlined" component="label" size="small" sx={{ mt: 1 }}>
                    Seleccionar Archivos
                    <input type="file" hidden multiple accept=".jpg,.jpeg,.png,.gif,.webp,.bmp" onChange={(e) => handleEvidenciasUpload(e.target.files)} />
                  </Button>
                </Box>
                {evidenciaPreviews.length > 0 && (
                  <Grid container spacing={1.5}>
                    {evidenciaPreviews.map((src, i) => {
                      const isImage = evidencias[i]?.type?.startsWith('image/');
                      return (
                        <Grid item xs={6} sm={4} md={3} key={i}>
                          <Box
                            sx={{
                              position: 'relative', border: '1px solid', borderColor: 'grey.200', borderRadius: 2, p: 0.5,
                              transition: 'all 0.2s', bgcolor: 'background.paper',
                              '&:hover': {
                                borderColor: 'primary.light', boxShadow: 1,
                                '& .preview-overlay': { opacity: 1 },
                              },
                            }}
                          >
                            <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                              <Box component="img" src={src} sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} />
                              <Box
                                className="preview-overlay"
                                sx={{
                                  position: 'absolute', inset: 0,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  bgcolor: 'rgba(0,0,0,0.4)',
                                  opacity: 0, transition: 'opacity 0.2s',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  if (isImage) {
                                    setPreviewImage({ url: src, name: evidencias[i]?.name || `Evidencia ${i + 1}` });
                                  } else {
                                    window.open(src, '_blank');
                                  }
                                }}
                              >
                                <ZoomInIcon sx={{ color: 'white', fontSize: 28 }} />
                              </Box>
                            </Box>
                            <Typography variant="caption" display="block" sx={{ mt: 0.25, px: 0.5, wordBreak: 'break-word', fontSize: '0.65rem', textAlign: 'center' }}>
                              {evidencias[i]?.name}
                            </Typography>
                            <IconButton size="small" sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }} onClick={() => eliminarEvidencia(i)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Paper>

              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Informe Técnico</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={5} label="Detalle Técnico" value={detalleTecnico} onChange={(e) => setDetalleTecnico(e.target.value)} placeholder="Detalle técnico del estado del equipo" required />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Responsable" value={responsable} InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" type="date" label="Fecha Recepción" value={fechaRecepcion} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                  <Button variant="contained" color="success" onClick={finalizarRetiro}>Finalizar Retiro</Button>
                </Stack>
              </Paper>
            </Stack>
          );
        }
        return null;
      })()}

      {historial.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <HistoryIcon color="action" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>Historial de Retiros</Typography>
            <Chip label={`${historial.length} registro(s)`} size="small" variant="outlined" sx={{ ml: 'auto' }} />
          </Stack>

          <Stack spacing={1.5}>
            {historial.map((retiro) => {
              const isExpanded = expandedHistorial === retiro.id;
              const estadoColor = retiro.estado === 'FINALIZADO' ? 'success' : retiro.estado === 'COBRADO' ? 'info' : retiro.estado === 'ANULADO' ? 'error' : 'warning';
              const EstadoIcon = retiro.estado === 'FINALIZADO' ? CheckCircleIcon : retiro.estado === 'ANULADO' ? CancelIcon : PendingIcon;

              return (
                <Card key={retiro.id} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Box
                    sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => setExpandedHistorial(isExpanded ? null : retiro.id)}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <EstadoIcon fontSize="small" color={estadoColor} />
                      <Chip
                        label={retiro.estado}
                        size="small"
                        color={estadoColor}
                        variant="filled"
                      />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {retiro.fecha_recepcion ? new Date(retiro.fecha_recepcion + 'T00:00:00').toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {retiro.responsable ? `por ${retiro.responsable}` : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {retiro.items?.length || 0} equipo(s)
                          {retiro.cobros?.length ? ` · ${new Set(retiro.cobros.map(c => c.serie || '')).size} equipo(s) con cobro` : ''}
                          {retiro.excepciones?.length ? ` · ${retiro.excepciones.length} excepción(es)` : ''}
                        </Typography>
                        <ExpandMoreIcon
                          fontSize="small"
                          sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s', color: 'text.secondary' }}
                        />
                      </Box>
                    </Stack>
                  </Box>

                  <Collapse in={isExpanded}>
                    <Box sx={{ px: 2, pb: 2, borderTop: '1px solid', borderColor: 'grey.100' }}>
                      <Stack direction="row" spacing={2} sx={{ mt: 1.5, mb: 1.5 }}>
                        <Typography variant="caption">
                          <Box component="span" fontWeight={600}>Incidencia:</Box> {retiro.tipo_incidencia?.nombre || '-'}
                        </Typography>
                        <Typography variant="caption">
                          <Box component="span" fontWeight={600}>Motivo:</Box> {retiro.motivo_retiro?.nombre || '-'}
                        </Typography>
                      </Stack>

                      {retiro.detalle_tecnico && (
                        <Typography variant="caption" display="block" sx={{ mb: 1.5, color: 'text.secondary' }}>
                          <Box component="span" fontWeight={600}>Detalle técnico:</Box> {retiro.detalle_tecnico}
                        </Typography>
                      )}

                      {retiro.items && retiro.items.length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Equipos</Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Modelo</TableCell>
                                <TableCell>Serie</TableCell>
                                <TableCell>MAC</TableCell>
                                <TableCell>Estado</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {retiro.items.map((item, i) => (
                                <TableRow key={item.id || i}>
                                  <TableCell><Typography variant="body2" fontWeight={500}>{item.modelo || '-'}</Typography></TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace' }}>{item.serie}</TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace' }}>{item.direccion_mac || '-'}</TableCell>
                                  <TableCell>
                                    <Chip label={item.estado_fisico} size="small" color={item.estado_fisico === 'Bueno' ? 'success' : item.estado_fisico === 'Regular' ? 'warning' : 'error'} />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      )}

                      {retiro.cobros && retiro.cobros.length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Cobros</Typography>
                          {(() => {
                            const groups: Record<string, typeof retiro.cobros> = {};
                            retiro.cobros.forEach(c => {
                              const key = c.serie || 'SIN_EQUIPO';
                              if (!groups[key]) groups[key] = [];
                              groups[key].push(c);
                            });
                            return Object.entries(groups).map(([serie, items]) => (
                              <Box key={serie} sx={{ mb: 1.5 }}>
                                <Typography variant="caption" fontWeight={600} sx={{ color: 'primary.main', mb: 0.5, display: 'block' }}>
                                  {serie === 'SIN_EQUIPO' ? 'Sin equipo' : `Equipo ${serie}`}
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Concepto</TableCell>
                                      <TableCell align="right">Cant.</TableCell>
                                      <TableCell align="right">Valor Unit.</TableCell>
                                      <TableCell align="right">Subtotal</TableCell>
                                      <TableCell align="right">IVA</TableCell>
                                      <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {items.map((c, i) => (
                                      <TableRow key={c.id || i}>
                                        <TableCell>{c.concepto}</TableCell>
                                        <TableCell align="right">{c.cantidad}</TableCell>
                                        <TableCell align="right">${Number(c.valor_unitario).toFixed(2)}</TableCell>
                                        <TableCell align="right">${Number(c.subtotal).toFixed(2)}</TableCell>
                                        <TableCell align="right">${Number(c.iva).toFixed(2)}</TableCell>
                                        <TableCell align="right"><Typography fontWeight={600}>${Number(c.total).toFixed(2)}</Typography></TableCell>
                                      </TableRow>
                                    ))}
                                    {(() => {
                                      const subtotal = items.reduce((s, c) => s + Number(c.total), 0);
                                      return (
                                        <TableRow>
                                          <TableCell colSpan={5} align="right"><Typography fontWeight={700}>Subtotal</Typography></TableCell>
                                          <TableCell align="right"><Typography fontWeight={700}>${subtotal.toFixed(2)}</Typography></TableCell>
                                        </TableRow>
                                      );
                                    })()}
                                  </TableBody>
                                </Table>
                              </Box>
                            ));
                          })()}
                          {(() => {
                            const total = retiro.cobros.reduce((s, c) => s + Number(c.total), 0);
                            return (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1 }}>
                                <Typography variant="subtitle2" fontWeight={700}>Total cobros: ${total.toFixed(2)}</Typography>
                              </Box>
                            );
                          })()}
                        </Box>
                      )}

                      {retiro.excepciones && retiro.excepciones.length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Excepciones</Typography>
                          <Stack spacing={1}>
                            {retiro.excepciones.map((exc, i) => (
                              <Box key={exc.id || i} sx={{ p: 1, bgcolor: '#fffbeb', borderRadius: 1.5, border: '1px solid', borderColor: '#fde68a' }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="caption" fontWeight={600}>{exc.motivo}</Typography>
                                  {exc.descripcion_motivo && (
                                    <Typography variant="caption" color="text.secondary">— {exc.descripcion_motivo}</Typography>
                                  )}
                                </Stack>
                                <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>{exc.detalle}</Typography>
                                {exc.concepto && (
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                    {exc.concepto}{exc.cantidad != null ? ` x${exc.cantidad}` : ''}
                                  </Typography>
                                )}
                                {exc.archivos_json && (() => {
                                  try {
                                    const parsed = JSON.parse(exc.archivos_json);
                                    if (Array.isArray(parsed) && parsed.length > 0) {
                                      return (
                                        <Grid container spacing={0.5} sx={{ mt: 0.5 }}>
                                          {parsed.map((f: any, fi: number) => (
                                            <Grid item xs={4} sm={3} md={2} key={fi}>
                                              <Box
                                                sx={{
                                                  position: 'relative',
                                                  border: '1px solid',
                                                  borderColor: 'grey.200',
                                                  borderRadius: 1.5,
                                                  p: 0.5,
                                                  cursor: f.url ? 'pointer' : 'default',
                                                  transition: 'all 0.2s',
                                                  bgcolor: 'background.paper',
                                                  '&:hover': {
                                                    borderColor: 'primary.light',
                                                    boxShadow: 1,
                                                    '& .overlay': { opacity: 1 },
                                                  },
                                                }}
                                                onClick={() => {
                                                  if (!f.url) return;
                                                  if (f.content_type?.startsWith('image/')) {
                                                    setPreviewImage({ url: f.url, name: f.nombre_original || `Archivo ${fi + 1}` });
                                                  } else {
                                                    window.open(f.url, '_blank');
                                                  }
                                                }}
                                              >
                                                <Box sx={{ position: 'relative', borderRadius: 0.5, overflow: 'hidden' }}>
                                                  {f.content_type?.startsWith('image/') && f.url ? (
                                                    <Box
                                                      component="img"
                                                      src={f.url}
                                                      alt={f.nombre_original || `Archivo ${fi + 1}`}
                                                      sx={{ width: '100%', height: 60, objectFit: 'cover', display: 'block' }}
                                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                                    />
                                                  ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 60, bgcolor: 'grey.100' }}>
                                                      {f.content_type?.includes('pdf') ? <PictureAsPdfIcon color="error" /> : <DescriptionIcon color="action" />}
                                                    </Box>
                                                  )}
                                                  <Box
                                                    className="overlay"
                                                    sx={{
                                                      position: 'absolute', inset: 0,
                                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                      bgcolor: 'rgba(0,0,0,0.4)',
                                                      opacity: 0, transition: 'opacity 0.2s',
                                                    }}
                                                  >
                                                    {f.content_type?.startsWith('image/') ? (
                                                      <ZoomInIcon sx={{ color: 'white', fontSize: 20 }} />
                                                    ) : (
                                                      <OpenInNewIcon sx={{ color: 'white', fontSize: 20 }} />
                                                    )}
                                                  </Box>
                                                </Box>
                                                <Typography variant="caption" display="block" sx={{ mt: 0.25, textAlign: 'center', wordBreak: 'break-word', fontSize: '0.6rem' }}>
                                                  {f.nombre_original || `Archivo ${fi + 1}`}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                          ))}
                                        </Grid>
                                      );
                                    }
                                  } catch {}
                                  return null;
                                })()}
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {retiro.evidencias && retiro.evidencias.length > 0 && (
                        <Box>
                          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                            Evidencias fotográficas
                            <Chip label={`${retiro.evidencias.length} archivo(s)`} size="small" variant="outlined" sx={{ ml: 1 }} />
                          </Typography>
                          <Grid container spacing={1}>
                            {retiro.evidencias.map((ev, i) => (
                              <Grid item xs={6} sm={4} md={3} key={ev.id || i}>
                                <Box
                                  sx={{
                                    position: 'relative',
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                    p: 0.5,
                                    cursor: ev.ruta_s3 ? 'pointer' : 'default',
                                    transition: 'all 0.2s',
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                      borderColor: 'primary.light',
                                      boxShadow: 1,
                                      '& .overlay': { opacity: 1 },
                                    },
                                  }}
                                  onClick={() => {
                                    if (!ev.ruta_s3) return;
                                    if (ev.content_type?.startsWith('image/')) {
                                      setPreviewImage({ url: ev.ruta_s3, name: ev.nombre_original || `Evidencia ${i + 1}` });
                                    } else {
                                      window.open(ev.ruta_s3, '_blank');
                                    }
                                  }}
                                >
                                  <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                                    {ev.content_type?.startsWith('image/') ? (
                                      <Box
                                        component="img"
                                        src={ev.ruta_s3}
                                        alt={ev.nombre_original || `Evidencia ${i + 1}`}
                                        sx={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}
                                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                      />
                                    ) : (
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100, bgcolor: 'grey.100' }}>
                                        <DescriptionIcon color="action" />
                                      </Box>
                                    )}
                                    <Box
                                      className="overlay"
                                      sx={{
                                        position: 'absolute', inset: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        bgcolor: 'rgba(0,0,0,0.4)',
                                        opacity: 0, transition: 'opacity 0.2s',
                                      }}
                                    >
                                      <ZoomInIcon sx={{ color: 'white', fontSize: 28 }} />
                                    </Box>
                                  </Box>
                                  <Typography variant="caption" display="block" sx={{ mt: 0.25, px: 0.5, wordBreak: 'break-word', textAlign: 'center', fontSize: '0.65rem' }}>
                                    {ev.nombre_original || `Evidencia ${i + 1}`}
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Card>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Image preview lightbox */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            bgcolor: 'black',
            borderRadius: 2,
            backgroundImage: 'repeating-conic-gradient(rgba(255,255,255,0.03) 0% 25%, transparent 0% 50%) 0 0 / 20px 20px',
          }
        }}
        sx={{ '& .MuiBackdrop-root': { bgcolor: 'rgba(0,0,0,0.85)' } }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
          <Typography variant="subtitle2" sx={{ color: 'grey.300', wordBreak: 'break-word', maxWidth: '80%' }}>{previewImage?.name || ''}</Typography>
          <Box>
            {previewImage?.url && (
              <IconButton
                component="a"
                href={previewImage.url}
                target="_blank"
                download
                sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}
                size="small"
              >
                <DownloadIcon />
              </IconButton>
            )}
            <IconButton onClick={() => setPreviewImage(null)} sx={{ color: 'white' }} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
          {previewImage?.url && (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.name}
              sx={{
                maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 1,
                boxShadow: '0 4px 30px rgba(0,0,0,0.5)',
              }}
              onClick={() => setPreviewImage(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Procesar Equipos (Cobro / Excepción) */}
      <Dialog open={cobroModalOpen} onClose={() => setCobroModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Procesar Equipos
          <IconButton onClick={() => setCobroModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Cliente: {cliente?.razon_social}</Typography>

          {procesarEquipos.length === 0 ? (
            <Alert severity="info">No hay equipos registrados en el retiro.</Alert>
          ) : (
            <Stack spacing={2} sx={{ mb: 3 }}>
              {procesarEquipos.map((eq, i) => {
                const showForm = (eq.cobrar || eq.excepcion) && !eq._collapsed;
                return (
                  <Card key={i} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Box sx={{ p: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack direction="row" spacing={1} alignItems="center">
                          {(eq.cobrar || eq.excepcion) && (
                            <IconButton size="small" onClick={() => {
                              const next = [...procesarEquipos];
                              next[i] = { ...next[i], _collapsed: !next[i]._collapsed };
                              setProcesarEquipos(next);
                            }}>
                              <ExpandMoreIcon sx={{ transform: eq._collapsed ? 'rotate(0deg)' : 'rotate(180deg)', transition: '0.2s' }} />
                            </IconButton>
                          )}
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>{eq.modelo || 'Equipo'}</Typography>
                            <Typography variant="caption" color="text.secondary">Serie: {eq.serie}{eq.mac ? ` / MAC: ${eq.mac}` : ''}</Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={2} onClick={(e) => e.stopPropagation()}>
                          {can('vision360.registrar_cobro') && (
                            <FormControlLabel
                              control={<Radio checked={eq.cobrar} size="small" />}
                              label={<Typography variant="body2" fontWeight={eq.cobrar ? 700 : 400}>Cobrar</Typography>}
                              onClick={(e) => {
                                e.stopPropagation();
                                const next = [...procesarEquipos];
                                const wasSelected = next[i].cobrar;
                                next[i] = { ...next[i], cobrar: !wasSelected, excepcion: false, costos: [], _collapsed: false };
                                setProcesarEquipos(next);
                              }}
                              sx={{ m: 0 }}
                            />
                          )}
                          {can('vision360.registrar_excepcion') && (
                            <FormControlLabel
                              control={<Radio checked={eq.excepcion} size="small" />}
                              label={<Typography variant="body2" fontWeight={eq.excepcion ? 700 : 400}>Excepción</Typography>}
                              onClick={(e) => {
                                e.stopPropagation();
                                const next = [...procesarEquipos];
                                const wasSelected = next[i].excepcion;
                                next[i] = { ...next[i], excepcion: !wasSelected, cobrar: false, _collapsed: false };
                                setProcesarEquipos(next);
                              }}
                              sx={{ m: 0 }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </Box>

                    <Collapse in={showForm}>
                      <Box sx={{ px: 2, pb: 2 }}>
                        {eq.cobrar && (
                          <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                            <Typography variant="caption" fontWeight={600} color="success.dark" sx={{ mb: 1, display: 'block' }}>Agregue los costos del equipo</Typography>

                            {eq.costos.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px 100px 36px', gap: 1, px: 1.5, mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">Concepto</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>Cant.</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>Subtotal</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>IVA 15%</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>Total</Typography>
                                  <Box />
                                </Box>
                                <Stack spacing={1}>
                                  {eq.costos.map((costo, ci) => (
                                    <Box key={ci} sx={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px 100px 36px', gap: 1, alignItems: 'center', bgcolor: 'white', borderRadius: 1, px: 1.5, py: 1, border: '1px solid', borderColor: 'grey.200' }}>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" fontWeight={600}>{costo.item}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">${Number(costo.valorUnitario).toFixed(2)} c/u</Typography>
                                      </Box>
                                      <TextField
                                        type="number" size="small" sx={{ width: 70, '& input': { textAlign: 'center', py: 1 } }}
                                        value={costo.cantidad}
                                        onChange={(e) => {
                                          const next = [...procesarEquipos];
                                          next[i] = { ...next[i], costos: next[i].costos.map((c, idx) => idx === ci ? { ...c, cantidad: Math.max(1, Number(e.target.value)) } : c) };
                                          setProcesarEquipos(next);
                                        }}
                                        inputProps={{ min: 1, style: { textAlign: 'center' } }}
                                      />
                                      <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', fontSize: '0.72rem' }}>${(costo.valorUnitario * costo.cantidad).toFixed(2)}</Typography>
                                      <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', color: 'error.main', fontSize: '0.72rem' }}>${(costo.valorUnitario * costo.cantidad * 0.15).toFixed(2)}</Typography>
                                      <Typography variant="body1" fontWeight={700} sx={{ textAlign: 'right', color: 'success.main', fontSize: '0.84rem' }}>${(costo.valorUnitario * costo.cantidad * 1.15).toFixed(2)}</Typography>
                                      <IconButton size="small" color="error" onClick={() => {
                                        const next = [...procesarEquipos];
                                        next[i] = { ...next[i], costos: next[i].costos.filter((_, idx) => idx !== ci) };
                                        setProcesarEquipos(next);
                                      }} sx={{ p: 0.5 }}><CloseIcon fontSize="small" /></IconButton>
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            )}

                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={6}>
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value=""
                                    displayEmpty
                                    onChange={(e) => {
                                      if (!e.target.value) return;
                                      const costo = costosCatalogo.find((c: any) => c.id === Number(e.target.value));
                                      if (!costo) return;
                                      const base = Number(costo.valor_total) / 1.15;
                                      const next = [...procesarEquipos];
                                      next[i] = { ...next[i], costos: [...next[i].costos, { id: costo.id, item: costo.item, valorUnitario: base, cantidad: 1 }] };
                                      setProcesarEquipos(next);
                                    }}
                                  >
                                    <MenuItem value=""><em>+ Agregar costo</em></MenuItem>
                                    {(() => {
                                      const ts = contrato?.tipo_servicio || '';
                                      const allowedGrupos = ts.includes('Fibra') ? ['FIBRA', 'VARIOS'] : ts.includes('Inalambrico') ? ['RADIO', 'VARIOS'] : ['FIBRA', 'RADIO', 'VARIOS'];
                                      const groups: Record<string, any[]> = {};
                                      costosCatalogo.filter((c: any) => c.activo !== false).forEach((c: any) => {
                                        const g = c.grupo || 'VARIOS';
                                        if (!groups[g]) groups[g] = [];
                                        groups[g].push(c);
                                      });
                                      const order = ['FIBRA', 'RADIO', 'VARIOS'].filter((g) => allowedGrupos.includes(g));
                                      return order.flatMap((g, gi) => {
                                        const items = groups[g];
                                        if (!items) return [];
                                        return [
                                          <MenuItem key={`hdr-${g}`} disabled sx={{ opacity: '1 !important', fontWeight: 700, fontSize: 12, color: 'text.secondary', pt: gi > 0 ? 1 : 0 }}>
                                            {g}
                                          </MenuItem>,
                                          ...items.map((c: any) => (
                                            <MenuItem key={c.id} value={c.id} sx={{ pl: 3 }}>
                                              {c.item} - ${Number(c.valor_total).toFixed(2)} (IVA incl.)
                                            </MenuItem>
                                          )),
                                        ];
                                      });
                                    })()}
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>
                          </Box>
                        )}

                        {eq.excepcion && (
                          <Stack spacing={2} sx={{ pt: 2 }}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Motivo de excepción</InputLabel>
                              <Select value={eq.excepcionMotivo} label="Motivo de excepción"
                                onChange={(e) => {
                                  const next = [...procesarEquipos];
                                  next[i] = { ...next[i], excepcionMotivo: e.target.value };
                                  setProcesarEquipos(next);
                                }}
                              >
                                <MenuItem value=""><em>Seleccione</em></MenuItem>
                                {motivosExcepcionCatalogo.map((m: any) => (
                                  <MenuItem key={m.id} value={m.nombre}>{m.nombre}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {eq.excepcionMotivo === 'Otro' && (
                              <TextField
                                size="small" fullWidth label="Descripción del motivo"
                                value={eq.excepcionDescripcion}
                                onChange={(e) => {
                                  const next = [...procesarEquipos];
                                  next[i] = { ...next[i], excepcionDescripcion: e.target.value };
                                  setProcesarEquipos(next);
                                }}
                              />
                            )}
                            <TextField
                              size="small" fullWidth multiline rows={3} label="Detalle de la situación"
                              placeholder="Describa detalladamente la situación del equipo"
                              value={eq.excepcionDetalle}
                              onChange={(e) => {
                                const next = [...procesarEquipos];
                                next[i] = { ...next[i], excepcionDetalle: e.target.value };
                                setProcesarEquipos(next);
                              }}
                            />
                            <Box>
                              <input
                                type="file" hidden multiple
                                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                                id={`exc-file-${i}`}
                                onChange={(e) => {
                                  const files = e.target.files;
                                  if (!files) return;
                                  const nuevos = Array.from(files);
                                  const next = [...procesarEquipos];
                                  next[i] = { ...next[i], excepcionArchivos: [...next[i].excepcionArchivos, ...nuevos] };
                                  nuevos.forEach(file => {
                                    const reader = new FileReader();
                                    reader.onload = (ev) => {
                                      const previews = [...next[i].excepcionArchivosPreviews, ev.target?.result as string];
                                      next[i] = { ...next[i], excepcionArchivosPreviews: previews };
                                      setProcesarEquipos([...next]);
                                    };
                                    reader.readAsDataURL(file);
                                  });
                                  setProcesarEquipos([...next]);
                                }}
                              />
                              <Button size="small" variant="outlined" component="label" htmlFor={`exc-file-${i}`} startIcon={<CloudUploadIcon />}>
                                Adjuntar documento
                              </Button>
                              {eq.excepcionArchivos.length > 0 && (
                                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                  {eq.excepcionArchivos.map((file, fi) => {
                                    const preview = eq.excepcionArchivosPreviews?.[fi];
                                    const isImage = file.type?.startsWith('image/');
                                    return (
                                      <Grid item xs={4} sm={3} md={2} key={fi}>
                                        <Box
                                          sx={{
                                            position: 'relative', border: '1px solid', borderColor: 'grey.200', borderRadius: 1.5, p: 0.5,
                                            transition: 'all 0.2s', bgcolor: 'background.paper',
                                            '&:hover': {
                                              borderColor: 'primary.light', boxShadow: 1,
                                              '& .exc-preview-overlay': { opacity: 1 },
                                            },
                                          }}
                                        >
                                          <Box sx={{ position: 'relative', borderRadius: 0.5, overflow: 'hidden' }}>
                                            {isImage && preview ? (
                                              <Box component="img" src={preview} alt={file.name} sx={{ width: '100%', height: 60, objectFit: 'cover', display: 'block' }} />
                                            ) : (
                                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 60, bgcolor: 'grey.100' }}>
                                                {file.type?.includes('pdf') ? <PictureAsPdfIcon color="error" /> : <DescriptionIcon color="action" />}
                                              </Box>
                                            )}
                                            <Box
                                              className="exc-preview-overlay"
                                              sx={{
                                                position: 'absolute', inset: 0,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                bgcolor: 'rgba(0,0,0,0.4)',
                                                opacity: 0, transition: 'opacity 0.2s',
                                                cursor: 'pointer',
                                              }}
                                              onClick={() => {
                                                if (isImage && preview) {
                                                  setPreviewImage({ url: preview, name: file.name || `Archivo ${fi + 1}` });
                                                }
                                              }}
                                            >
                                              {isImage ? (
                                                <ZoomInIcon sx={{ color: 'white', fontSize: 20 }} />
                                              ) : (
                                                <OpenInNewIcon sx={{ color: 'white', fontSize: 20 }} />
                                              )}
                                            </Box>
                                          </Box>
                                          <Typography variant="caption" display="block" sx={{ mt: 0.25, textAlign: 'center', fontSize: '0.6rem', wordBreak: 'break-word' }}>
                                            {file.name}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 18, height: 18 }}
                                            onClick={() => {
                                              const next = [...procesarEquipos];
                                              next[i] = {
                                                ...next[i],
                                                excepcionArchivos: next[i].excepcionArchivos.filter((_, idx) => idx !== fi),
                                                excepcionArchivosPreviews: next[i].excepcionArchivosPreviews.filter((_, idx) => idx !== fi),
                                              };
                                              setProcesarEquipos(next);
                                            }}
                                          >
                                            <DeleteIcon sx={{ fontSize: 12 }} />
                                          </IconButton>
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              )}
                            </Box>
                          </Stack>
                        )}
                      </Box>
                    </Collapse>
                  </Card>
                );
              })}
            </Stack>
          )}

          {/* Totales generales */}
          {(() => {
            const subTotal = procesarEquipos.reduce((s, e) => s + (e.cobrar ? e.costos.reduce((sum, c) => sum + c.valorUnitario * c.cantidad, 0) : 0), 0);
            if (subTotal <= 0) return null;
            const ivaTotal = subTotal * 0.15;
            const granTotal = subTotal + ivaTotal;
            return (
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid', borderColor: '#bbf7d0' }}>
                <Stack direction="row" spacing={4} justifyContent="flex-end">
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ fontSize: '0.84rem' }}>${subTotal.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">IVA 15%</Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ color: 'error.main', fontSize: '0.84rem' }}>${ivaTotal.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">Total general</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'success.main', fontSize: '1.05rem' }}>${granTotal.toFixed(2)}</Typography>
                  </Box>
                </Stack>
              </Paper>
            );
          })()}

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" color="success" onClick={procesarEquiposHandler} size="large">
            {procesarEquipos.some(e => e.cobrar || e.excepcion)
              ? `Confirmar (${procesarEquipos.filter(e => e.cobrar).length} cobro(s), ${procesarEquipos.filter(e => e.excepcion).length} excepción(es))`
              : 'Sin cobro ni excepción'}
          </Button>
          <Button variant="outlined" color="error" onClick={() => setCobroModalOpen(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
